import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Box, Typography, Chip } from '@material-ui/core';
import type { GroupedDimension } from '../../../../_models/schedule.model';
import { getStatusColors, getChartTheme, useThemeVersion } from '../../../../theme/chartTheme';

interface RoomUtilizationChartProps {
  groupedByRoom: GroupedDimension[];
}

const RoomUtilizationChart: React.FC<RoomUtilizationChartProps> = ({ groupedByRoom }) => {
  const [sortBy, setSortBy] = useState<'sessions' | 'alphabetical'>('sessions');
  const themeVersion = useThemeVersion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const statusColors = React.useMemo(() => getStatusColors(), [themeVersion]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const chartGrid = React.useMemo(() => getChartTheme().grid, [themeVersion]);
  const [viewType, setViewType] = useState<'top' | 'bottom'>('top');
  const [showCount, setShowCount] = useState(10);

  const sortedData = [...groupedByRoom]
    .sort((a, b) => {
      if (sortBy === 'sessions') {
        return viewType === 'top' ? b.count - a.count : a.count - b.count;
      }
      return a.name.localeCompare(b.name);
    })
    .slice(0, showCount)
    .map((room) => ({
      name: room.name.length > 20 ? room.name.substring(0, 20) + '...' : room.name,
      fullName: room.name,
      sessions: room.count,
      hours: room.slots.reduce((acc, slot) => {
        const start = slot.startTime ? new Date(`2000-01-01T${slot.startTime}`) : null;
        const end = slot.endTime ? new Date(`2000-01-01T${slot.endTime}`) : null;
        if (start && end) {
          const diff = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
          return acc + diff;
        }
        return acc;
      }, 0),
    }));

  const getBarColor = (value: number, maxValue: number) => {
    const percentage = (value / maxValue) * 100;
    if (percentage >= 80) return statusColors.success; // High utilization
    if (percentage >= 50) return statusColors.info; // Medium utilization
    if (percentage >= 30) return statusColors.warning; // Low-medium utilization
    return statusColors.danger; // Low utilization
  };

  const maxSessions = Math.max(...sortedData.map((d) => d.sessions), 1);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            background: 'white',
            padding: '12px 16px',
            border: '1px solid var(--border-default)',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          }}
        >
          <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--text-strong)', marginBottom: 4 }}>
            {data.fullName}
          </Typography>
          <Typography variant="body2" style={{ color: 'var(--text-muted)', marginBottom: 2 }}>
            Sessions: {data.sessions}
          </Typography>
          <Typography variant="body2" style={{ color: 'var(--text-muted)' }}>
            Hours: {data.hours.toFixed(1)}h
          </Typography>
        </div>
      );
    }
    return null;
  };

  if (groupedByRoom.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <Typography variant="body1" style={{ color: 'var(--text-muted)' }}>
          No room data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap">
          <Box display="flex" flexWrap="wrap" style={{ marginBottom: 8 }}>
            <Chip
              label="Sort: Most Used"
              onClick={() => setSortBy('sessions')}
              style={{
                background: sortBy === 'sessions' ? 'var(--brand-primary)' : 'var(--border-default)',
                color: sortBy === 'sessions' ? 'white' : 'var(--text-muted)',
                fontWeight: 600,
                cursor: 'pointer',
                marginRight: 8,
                marginBottom: 4,
              }}
            />
            <Chip
              label="Sort: A-Z"
              onClick={() => setSortBy('alphabetical')}
              style={{
                background: sortBy === 'alphabetical' ? 'var(--brand-primary)' : 'var(--border-default)',
                color: sortBy === 'alphabetical' ? 'white' : 'var(--text-muted)',
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: 4,
              }}
            />
          </Box>
        </Box>

        <Box display="flex" flexWrap="wrap" style={{ marginBottom: 8 }}>
          <Typography variant="caption" style={{ color: 'var(--text-muted)', marginRight: 12, alignSelf: 'center', fontWeight: 600 }}>
            Top Performers:
          </Typography>
          {[5, 10].map((num) => (
            <Chip
              key={`top-${num}`}
              label={`Top ${num}`}
              onClick={() => {
                setViewType('top');
                setShowCount(num);
              }}
              size="small"
              style={{
                background: viewType === 'top' && showCount === num ? 'var(--status-success)' : 'var(--border-default)',
                color: viewType === 'top' && showCount === num ? 'white' : 'var(--text-muted)',
                fontWeight: 600,
                cursor: 'pointer',
                marginRight: 8,
                marginBottom: 4,
              }}
            />
          ))}
        </Box>

        <Box display="flex" flexWrap="wrap">
          <Typography variant="caption" style={{ color: 'var(--status-danger)', marginRight: 12, alignSelf: 'center', fontWeight: 600 }}>
            Under-Utilized:
          </Typography>
          {[5, 10].map((num) => (
            <Chip
              key={`bottom-${num}`}
              label={`Bottom ${num}`}
              onClick={() => {
                setViewType('bottom');
                setShowCount(num);
              }}
              size="small"
              style={{
                background: viewType === 'bottom' && showCount === num ? 'var(--status-danger)' : 'var(--border-default)',
                color: viewType === 'bottom' && showCount === num ? 'white' : 'var(--text-muted)',
                fontWeight: 600,
                cursor: 'pointer',
                marginRight: 8,
                marginBottom: 4,
              }}
            />
          ))}
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={sortedData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            height={100}
            style={{ fontSize: '12px', fill: 'var(--text-muted)' }}
          />
          <YAxis style={{ fontSize: '12px', fill: 'var(--text-muted)' }} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="sessions" radius={[8, 8, 0, 0]}>
            {sortedData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getBarColor(entry.sessions, maxSessions)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <Box mt={2} display="flex" justifyContent="center" style={{ gap: '24px' }} flexWrap="wrap">
        <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
          <div style={{ width: '16px', height: '16px', background: 'var(--status-danger)', borderRadius: '4px' }} />
          <Typography variant="caption" style={{ color: 'var(--text-muted)' }}>
            Low (&lt;30%)
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
          <div style={{ width: '16px', height: '16px', background: 'var(--status-warning)', borderRadius: '4px' }} />
          <Typography variant="caption" style={{ color: 'var(--text-muted)' }}>
            Medium (30-50%)
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
          <div style={{ width: '16px', height: '16px', background: 'var(--status-info)', borderRadius: '4px' }} />
          <Typography variant="caption" style={{ color: 'var(--text-muted)' }}>
            Good (50-80%)
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
          <div style={{ width: '16px', height: '16px', background: 'var(--status-success)', borderRadius: '4px' }} />
          <Typography variant="caption" style={{ color: 'var(--text-muted)' }}>
            High (≥80%)
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default RoomUtilizationChart;
